// A fresh `__main__` module for one Python run on a shared Pyodide engine.
//
// Runners that reuse one engine (the /workspace TerminalContext, the wizard preview) give
// every run its own globals, so a function the student deleted, or an earlier run's
// `result`, cannot carry over and pass. A bare dict is not enough, though: code that looks
// itself up through sys.modules['__main__'] - pickle of the student's own classes,
// doctest.testmod(), `import __main__` - would find Pyodide's real (empty) __main__ instead.
// So each run gets a real module registered as __main__, and the previous one is put back
// when the run is released.
//
//   const run = freshMainGlobals(pyodide)
//   await pyodide.runPythonAsync(source, { globals: run.globals })
//   ...read results from run.globals...
//   run.release()

const SETUP = `
def __vizpi_enter_main():
    import builtins, sys, types
    __vizpi_saved.append(sys.modules.get('__main__'))
    module = types.ModuleType('__main__')
    # As in a real __main__ (exec would otherwise insert the builtins dict).
    module.__dict__['__builtins__'] = builtins
    sys.modules['__main__'] = module
    return module.__dict__

def __vizpi_exit_main():
    import sys
    previous = __vizpi_saved.pop() if __vizpi_saved else None
    if previous is not None:
        sys.modules['__main__'] = previous

__vizpi_saved = []
`

const helpersByEngine = new WeakMap()

const helpersFor = (pyodide) => {
  let helpers = helpersByEngine.get(pyodide)
  if (!helpers) {
    // Defined in a private namespace, so nothing is added to the student's globals.
    const namespace = pyodide.globals.get('dict')()
    pyodide.runPython(SETUP, { globals: namespace })
    helpers = { enter: namespace.get('__vizpi_enter_main'), exit: namespace.get('__vizpi_exit_main') }
    namespace.destroy()
    helpersByEngine.set(pyodide, helpers)
  }
  return helpers
}

export const freshMainGlobals = (pyodide) => {
  const { enter, exit } = helpersFor(pyodide)
  const globals = enter()
  let released = false
  return {
    globals,
    release: () => {
      if (released) return
      released = true
      try {
        exit()
      } finally {
        globals.destroy?.()
      }
    },
  }
}
